import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { stagger, fadeUp } from '@/lib/animations';
import {
  Package,
  TrendingUp,
  Truck,
  DollarSign,
  Clock,
  CheckCircle2,
  AlertCircle,
  FileText,
  Ship,
  Plane,
  Train,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { PageHeader, MetricCard, KPIGrid, StatusBadge } from '@/components/common';
import { useAuth } from '@/context/AuthContext';
import {
  tmsAnalyticsApi,
  ShipmentKPIs,
  CarrierPerformance as CarrierPerformanceType,
} from '@/api/services/analytics.api';
import {
  tmsCarriersApi,
  CarriersSummary,
  rateEngineApi,
  rfqApi,
  PendingApproval,
} from '@/api/services/tms.api';
import { trackingApi, ShipmentTracking } from '@/api/services/tracking.api';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  Legend,
} from 'recharts';
import { formatDate } from '@/lib/utils';

// Mock data interfaces
interface DashboardMetrics {
  activeShipments: number;
  onTimeDelivery: number;
  activeCarriers: number;
  freightSpend: number;
  avgTransitTime: number;
  pendingApprovals: number;
  expiringRates: number;
  costPerShipment: number;
}

// CarrierPerformance is now imported from analytics.api (CarrierPerformanceType)

interface FreightSpendData {
  month: string;
  amount: number;
}

interface ModeDistribution {
  mode: string;
  count: number;
  percentage: number;
}

// Mock data (temporary - until backend endpoints exist)
const mockFreightSpend: FreightSpendData[] = [
  { month: 'Jul', amount: 980000 },
  { month: 'Aug', amount: 1050000 },
  { month: 'Sep', amount: 1120000 },
  { month: 'Oct', amount: 1180000 },
  { month: 'Nov', amount: 1220000 },
  { month: 'Dec', amount: 1245000 },
];

const mockModeDistribution: ModeDistribution[] = [
  { mode: 'Ocean', count: 52, percentage: 41 },
  { mode: 'TruckFTL', count: 38, percentage: 30 },
  { mode: 'Air', count: 22, percentage: 17 },
  { mode: 'TruckLTL', count: 10, percentage: 8 },
  { mode: 'Rail', count: 5, percentage: 4 },
];

// Chart colors - using CSS variables
const MODE_COLORS: Record<string, string> = {
  Ocean: 'var(--color-success-500)',
  TruckFTL: 'var(--color-primary-500)',
  Air: 'var(--color-purple-500)',
  TruckLTL: 'var(--color-primary-400)',
  Rail: 'var(--color-warning-500)',
};

const MODE_ICONS: Record<string, React.ReactNode> = {
  Ocean: <Ship className="w-4 h-4" />,
  TruckFTL: <Truck className="w-4 h-4" />,
  TruckLTL: <Truck className="w-4 h-4" />,
  Air: <Plane className="w-4 h-4" />,
  Rail: <Train className="w-4 h-4" />,
};

const PIE_COLORS = [
  'var(--color-success-500)',
  'var(--color-primary-500)',
  'var(--color-purple-500)',
  'var(--color-primary-400)',
  'var(--color-warning-500)',
];

// Shipment status mapping for StatusBadge
const shipmentStatusMap: Record<string, string> = {
  'In Transit': 'in_transit',
  'At Port': 'pending',
  Loading: 'processing',
  Delayed: 'delayed',
  Delivered: 'delivered',
};

export default function TMSDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();

  // Loading state
  const [loading, setLoading] = useState(true);

  // Real data from API
  const [kpis, setKpis] = useState<ShipmentKPIs | null>(null);
  const [carrierSummary, setCarrierSummary] = useState<CarriersSummary | null>(null);
  const [rateSummary, setRateSummary] = useState<{ expiringRates: number } | null>(null);
  const [approvals, setApprovals] = useState<PendingApproval[]>([]);
  const [activeShipments, setActiveShipments] = useState<ShipmentTracking[]>([]);
  const [carrierPerformance, setCarrierPerformance] = useState<CarrierPerformanceType[]>([]);

  // Mock data (temporary - until backend endpoints exist)
  const [freightSpend] = useState<FreightSpendData[]>(mockFreightSpend);
  const [modeDistribution] = useState<ModeDistribution[]>(mockModeDistribution);

  // Load dashboard data
  useEffect(() => {
    async function loadDashboard() {
      if (!user?.companyId) return;

      setLoading(true);

      // Calculate date range for carrier performance (last 30 days)
      const today = new Date();
      const thirtyDaysAgo = new Date(today);
      thirtyDaysAgo.setDate(today.getDate() - 30);

      const results = await Promise.allSettled([
        tmsAnalyticsApi.getShipmentKPIs(), // [0] KPIs
        tmsCarriersApi.getSummary(user.companyId), // [1] Carrier summary
        rateEngineApi.getSummary(), // [2] Rate summary
        rfqApi.getMyPendingApprovals(), // [3] Pending approvals
        trackingApi.getActiveShipments({
          companyId: user.companyId,
          status: 'IN_TRANSIT,BOOKED,DELAYED',
          take: 5,
        }), // [4] Active shipments
        tmsAnalyticsApi.getCarrierPerformance({
          startDate: thirtyDaysAgo.toISOString().split('T')[0],
          endDate: today.toISOString().split('T')[0],
        }), // [5] Carrier performance
      ]);

      // Helper to extract successful results
      const extract = <T,>(result: PromiseSettledResult<T>): T | null => {
        if (result.status === 'fulfilled') return result.value;
        console.error('API error:', result.reason);
        return null;
      };

      // Set state from API responses
      setKpis(extract(results[0]));
      setCarrierSummary(extract(results[1]));
      setRateSummary(extract(results[2]));
      setApprovals(extract(results[3]) || []);

      // Handle active shipments response (it has .data wrapper)
      const shipmentsResult = extract(results[4]);
      setActiveShipments(shipmentsResult?.data || []);

      setCarrierPerformance(extract(results[5]) || []);
      setLoading(false);
    }

    loadDashboard();
  }, [user?.companyId]);

  // Derive metrics from API responses
  const metrics: DashboardMetrics = {
    activeShipments: kpis?.totalShipments ?? 0,
    onTimeDelivery: kpis?.onTimeDeliveryRate ?? 0,
    activeCarriers: carrierSummary?.byStatus.ACTIVE ?? 0,
    avgTransitTime: kpis?.avgTransitDays ?? 0,
    pendingApprovals: approvals.length,
    expiringRates: rateSummary?.expiringRates ?? 0,
    // TODO: Add backend endpoints for these
    freightSpend: 0,
    costPerShipment: 0,
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('en-US').format(num);
  };

  return (
    <motion.main className="space-y-5" variants={stagger} initial="hidden" animate="show">
      <motion.div variants={fadeUp}>
        <PageHeader
          title="TMS Dashboard"
          description="Current freight position across carriers, shipments in transit, and open quotations."
          actions={
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => navigate('/shipment-planner')}>
                <Package className="w-4 h-4 mr-2" />
                Plan Shipment
              </Button>
              <Button onClick={() => navigate('/tms/rfq/new')}>
                <FileText className="w-4 h-4 mr-2" />
                New RFQ
              </Button>
            </div>
          }
        />
      </motion.div>

      {/* Primary KPIs */}
      <div className="mt-6">
        <KPIGrid columns={4}>
          <MetricCard
            label="Active Shipments"
            value={loading ? '...' : formatNumber(metrics.activeShipments)}
            sentiment="info"
            icon={<Package className="w-5 h-5" />}
          />
          <MetricCard
            label="On-Time Delivery"
            value={loading ? '...' : `${metrics.onTimeDelivery.toFixed(1)}%`}
            sentiment="success"
            icon={<CheckCircle2 className="w-5 h-5" />}
          />
          <MetricCard
            label="Active Carriers"
            value={loading ? '...' : formatNumber(metrics.activeCarriers)}
            sentiment="warning"
            icon={<Truck className="w-5 h-5" />}
          />
          <MetricCard
            label="Total Freight Spend"
            value={loading ? '...' : '—'}
            subtitle={loading ? undefined : 'Coming Soon'}
            sentiment="info"
            icon={<DollarSign className="w-5 h-5" />}
          />
        </KPIGrid>
      </div>

      {/* Secondary Metrics */}
      <div className="mt-6">
        <KPIGrid columns={4}>
          <MetricCard
            label="Avg Transit Time"
            value={loading ? '...' : `${metrics.avgTransitTime.toFixed(1)} days`}
            sentiment="neutral"
            icon={<Clock className="h-5 w-5" />}
          />
          <MetricCard
            label="Pending Approvals"
            value={loading ? '...' : formatNumber(metrics.pendingApprovals)}
            sentiment={metrics.pendingApprovals > 0 ? 'warning' : 'success'}
            icon={<AlertCircle className="h-5 w-5" />}
          />
          <MetricCard
            label="Rate Expiries"
            value={loading ? '...' : formatNumber(metrics.expiringRates)}
            sentiment={metrics.expiringRates > 0 ? 'danger' : 'success'}
            icon={<FileText className="h-5 w-5" />}
          />
          <MetricCard
            label="Cost per Shipment"
            value={loading ? '...' : '—'}
            subtitle={loading ? undefined : 'Coming Soon'}
            sentiment="success"
            icon={<TrendingUp className="h-5 w-5" />}
          />
        </KPIGrid>
      </div>

      {/* Two-Column Layout: Active Shipments + Carrier Performance */}
      <motion.div variants={fadeUp} className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
        {/* Active Shipments Table */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <h3 className="text-base font-semibold text-foreground">Active Shipments</h3>
            <Button variant="ghost" size="sm" onClick={() => navigate('/shipments')}>
              View All
            </Button>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-3">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="p-3 bg-muted/30 rounded-lg animate-pulse h-24" />
                ))}
              </div>
            ) : activeShipments.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No active shipments found
              </div>
            ) : (
              <div className="space-y-3">
                {activeShipments.map((shipment) => {
                  // Calculate progress based on actual/estimated arrival
                  const now = new Date().getTime();
                  const departure = shipment.actualDeparture || shipment.estimatedDeparture;
                  const arrival = shipment.estimatedArrival;
                  let progress = 50; // default

                  if (departure && arrival) {
                    const depTime = new Date(departure).getTime();
                    const arrTime = new Date(arrival).getTime();
                    const total = arrTime - depTime;
                    const elapsed = now - depTime;
                    progress = Math.min(Math.max((elapsed / total) * 100, 0), 100);
                  }

                  // Map transport mode to icon
                  const modeKey =
                    shipment.transportMode === 'OCEAN'
                      ? 'Ocean'
                      : shipment.transportMode === 'AIR'
                        ? 'Air'
                        : shipment.transportMode === 'ROAD_FTL'
                          ? 'TruckFTL'
                          : shipment.transportMode === 'ROAD_LTL'
                            ? 'TruckLTL'
                            : shipment.transportMode === 'RAIL'
                              ? 'Rail'
                              : 'TruckFTL';

                  // Origin and destination display
                  const originDisplay =
                    `${shipment.origin.address?.city || ''}, ${shipment.origin.address?.state || ''}`
                      .trim()
                      .replace(/, $/, '');
                  const destDisplay =
                    `${shipment.destination.address?.city || ''}, ${shipment.destination.address?.state || ''}`
                      .trim()
                      .replace(/, $/, '');

                  return (
                    <div
                      key={shipment.id}
                      className="p-3 bg-muted/30 rounded-lg cursor-pointer hover:bg-muted/50 transition-colors"
                      onClick={() => navigate(`/shipments/${shipment.id}`)}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <div className="font-medium text-sm">{shipment.shipmentNumber}</div>
                          <div className="text-xs text-muted-foreground mt-1">
                            {originDisplay} → {destDisplay}
                          </div>
                        </div>
                        <StatusBadge
                          status={shipmentStatusMap[shipment.status] || 'pending'}
                          label={shipment.status}
                        />
                      </div>
                      <div className="flex items-center justify-between text-xs text-muted-foreground mb-2">
                        <div className="flex items-center gap-2">
                          {MODE_ICONS[modeKey]}
                          <span>{shipment.carrier || 'Unknown Carrier'}</span>
                        </div>
                        {shipment.estimatedArrival && (
                          <span>ETA: {formatDate(shipment.estimatedArrival)}</span>
                        )}
                      </div>
                      <div className="w-full bg-muted rounded-full h-1.5">
                        <div
                          className="bg-primary h-1.5 rounded-full transition-all"
                          style={{ width: `${progress}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Carrier Performance Chart */}
        <Card>
          <CardHeader>
            <h3 className="text-base font-semibold text-foreground">Carrier Performance</h3>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="h-80 flex items-center justify-center">
                <div className="animate-pulse text-muted-foreground">Loading carrier data...</div>
              </div>
            ) : carrierPerformance.length === 0 ? (
              <div className="h-80 flex items-center justify-center text-muted-foreground">
                No carrier performance data available
              </div>
            ) : (
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={carrierPerformance} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis type="number" domain={[0, 100]} />
                    <YAxis
                      dataKey="carrierName"
                      type="category"
                      width={100}
                      tick={{ fontSize: 12 }}
                    />
                    <Tooltip
                      formatter={(value: number) => [`${value.toFixed(1)}%`, 'On-Time Delivery']}
                      content={({ active, payload }) => {
                        if (active && payload && payload.length) {
                          const data = payload[0].payload as CarrierPerformanceType;
                          return (
                            <div className="bg-background border rounded-lg shadow-overlay p-3">
                              <p className="font-medium">{data.carrierName}</p>
                              <p className="text-sm text-muted-foreground">
                                On-Time: {data.onTimeRate.toFixed(1)}%
                              </p>
                              <p className="text-sm text-muted-foreground">
                                Shipments: {data.totalShipments}
                              </p>
                            </div>
                          );
                        }
                        return null;
                      }}
                    />
                    <Bar dataKey="onTimeRate" fill="#3B82F6" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>
      </motion.div>

      {/* Bottom Row: Freight Spend Trend + Mode Distribution */}
      <motion.div variants={fadeUp} className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
        {/* Freight Spend Trend */}
        {/* TODO: Replace mockdata with real API endpoint GET /tms/analytics/freight-spend */}
        <Card>
          <CardHeader>
            <h3 className="text-base font-semibold text-foreground">
              Freight Spend Trend (6 Months)
              <span className="ml-2 text-xs text-muted-foreground font-normal">(Sample Data)</span>
            </h3>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={freightSpend}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} tickFormatter={(value) => formatCurrency(value)} />
                  <Tooltip formatter={(value: number) => [formatCurrency(value), 'Spend']} />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="amount"
                    name="Freight Spend"
                    stroke="#3B82F6"
                    strokeWidth={3}
                    dot={{ fill: '#3B82F6', r: 4 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Mode Distribution */}
        {/* TODO: Replace mockdata with real API endpoint GET /tms/analytics/mode-distribution */}
        <Card>
          <CardHeader>
            <h3 className="text-base font-semibold text-foreground">
              Shipments by Mode
              <span className="ml-2 text-xs text-muted-foreground font-normal">(Sample Data)</span>
            </h3>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={modeDistribution}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={90}
                    paddingAngle={2}
                    dataKey="count"
                    nameKey="mode"
                    label={({ mode, percentage }) => `${mode}: ${percentage}%`}
                  >
                    {modeDistribution.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={MODE_COLORS[entry.mode] || PIE_COLORS[index % PIE_COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(value: number, name: string) => [formatNumber(value), name]}
                  />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </motion.main>
  );
}
